import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import TrendingDownRoundedIcon from '@mui/icons-material/TrendingDownRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import { learningAPI } from '../../services/api';

const formatDate = (value, includeTime = false) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, includeTime
    ? { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }
    : { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatScore = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const numeric = Number(value);
  return Number.isFinite(numeric) ? `${Math.round(numeric * 10) / 10}%` : '—';
};

const feedbackLabels = {
  no_feedback: 'No feedback',
  feedback_added: 'Feedback added',
  student_viewed: 'Student viewed',
  student_acknowledged: 'Acknowledged',
  follow_up_requested: 'Follow-up requested',
  resolved: 'Resolved',
};

function MetricList({ title, rows, emptyMessage }) {
  return (
    <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 3, height: '100%' }}>
      <Typography variant="h6" sx={{ fontWeight: 900 }}>{title}</Typography>
      {rows.length ? (
        <Stack spacing={1.5} sx={{ mt: 2 }}>
          {rows.map((row) => (
            <Box key={row.key || row.label}>
              <Stack direction="row" justifyContent="space-between" spacing={1}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{row.label}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 900 }}>{formatScore(row.averageScore)}</Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                <LinearProgress variant="determinate" value={Math.max(0, Math.min(100, row.averageScore || 0))} sx={{ flex: 1, height: 6, borderRadius: 99 }} />
                <Typography variant="caption" color="text.secondary">{row.count} observation{row.count === 1 ? '' : 's'}</Typography>
              </Stack>
            </Box>
          ))}
        </Stack>
      ) : <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>{emptyMessage}</Typography>}
    </Paper>
  );
}

function StudentDetailPage() {
  const { studentId } = useParams();
  const [analytics, setAnalytics] = useState(null);
  const [histories, setHistories] = useState([]);
  const [historyPagination, setHistoryPagination] = useState({ page: 1, total: 0, hasMore: false });
  const [loadingOlderHistories, setLoadingOlderHistories] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadStudent = useCallback(async () => {
    setLoading(true);
    try {
      const [analyticsResponse, historiesResponse] = await Promise.all([
        learningAPI.getStudentAnalytics(studentId),
        learningAPI.listStudentHistories(studentId, { page: 1, limit: 50 }),
      ]);
      setAnalytics(analyticsResponse.data?.analytics || null);
      setHistories(historiesResponse.data?.histories || []);
      setHistoryPagination(historiesResponse.data?.pagination || {
        page: 1,
        total: historiesResponse.data?.histories?.length || 0,
        hasMore: false,
      });
      setError('');
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Unable to load this learner.');
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    loadStudent();
  }, [loadStudent]);

  const loadOlderHistories = async () => {
    if (loadingOlderHistories || !historyPagination.hasMore) return;
    setLoadingOlderHistories(true);
    try {
      const response = await learningAPI.listStudentHistories(studentId, {
        page: historyPagination.page + 1,
        limit: 50,
      });
      const olderHistories = response.data?.histories || [];
      setHistories((current) => {
        const byId = new Map(current.map((history) => [history.id, history]));
        olderHistories.forEach((history) => byId.set(history.id, history));
        return Array.from(byId.values());
      });
      setHistoryPagination(response.data?.pagination || {
        page: historyPagination.page + 1,
        total: histories.length + olderHistories.length,
        hasMore: false,
      });
      setError('');
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Unable to load older completed cases.');
    } finally {
      setLoadingOlderHistories(false);
    }
  };

  const groupedCaseRows = useMemo(() => analytics?.cases || [], [analytics]);

  if (loading) return <Box sx={{ minHeight: '60vh', display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>;

  if (!analytics) {
    return (
      <Container maxWidth="md" sx={{ py: 5 }}>
        <Alert severity="error" action={<Button component={RouterLink} to="/university/teaching?tab=students">Back to students</Button>}>
          {error || 'Learner not found.'}
        </Alert>
      </Container>
    );
  }

  const { student, summary, benchmarks, strengths, priorities, viva, recurringOmissions } = analytics;
  const trend = Number(summary.recentTrend);
  const trendKnown = Number.isFinite(trend);

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 3, md: 5 } }}>
      <Button component={RouterLink} to="/university/teaching?tab=students" startIcon={<ArrowBackRoundedIcon />} sx={{ mb: 2 }}>
        Teaching workspace
      </Button>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 900 }}>{student.name || 'Learner profile'}</Typography>
          <Typography color="text.secondary">{student.email}</Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1.25 }}>
            {(student.groups || []).map((group) => (
              <Chip key={group.id} component={RouterLink} clickable to={`/university/teaching/cohorts/${group.id}`} label={group.name} />
            ))}
            <Chip
              variant="outlined"
              label={`${summary.evidenceLevel} evidence`}
              color={summary.evidenceLevel === 'established' ? 'success' : summary.evidenceLevel === 'emerging' ? 'info' : 'warning'}
              sx={{ textTransform: 'capitalize' }}
            />
          </Stack>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480 }}>
            Insights use recorded marking data, not generative AI. Peer averages are learner-balanced and hidden until at least five learners are available.
        </Typography>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          ['Average score', formatScore(summary.averageScore), `${summary.totalAttempts} total attempts`],
          ['Best score', formatScore(summary.bestScore), `${summary.uniqueCases} unique cases`],
          ['Recent trend', trendKnown ? `${trend > 0 ? '+' : ''}${trend.toFixed(1)} pts` : '—', trendKnown ? 'Last 5 vs previous 5 attempts' : 'Needs at least 10 attempts'],
          ['Last attempt', formatDate(summary.lastAttemptAt), `${summary.analysedAttempts} attempts analysed`],
        ].map(([label, value, helper]) => (
          <Grid item xs={12} sm={6} lg={3} key={label}>
            <Card variant="outlined" sx={{ height: '100%', borderRadius: 3 }}>
              <CardContent>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, textTransform: 'uppercase' }}>{label}</Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                  <Typography variant="h4" sx={{ fontWeight: 900 }}>{value}</Typography>
                  {label === 'Recent trend' && trendKnown && (trend >= 0 ? <TrendingUpRoundedIcon color="success" /> : <TrendingDownRoundedIcon color="error" />)}
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{helper}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Tabs value={activeTab} onChange={(_event, tab) => setActiveTab(tab)} variant="scrollable" scrollButtons="auto" sx={{ px: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Tab icon={<InsightsRoundedIcon />} iconPosition="start" label="Performance insights" />
          <Tab icon={<HistoryRoundedIcon />} iconPosition="start" label={`Completed cases (${historyPagination.total || histories.length})`} />
        </Tabs>
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {activeTab === 0 ? (
            <Stack spacing={3}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={5}>
                  <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 3, height: '100%' }}>
                    <Typography variant="h6" sx={{ fontWeight: 900 }}>Peer comparison</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Exam-mode attempts only.</Typography>
                    <Stack spacing={1.5} sx={{ mt: 2 }}>
                      {[
                        ['Learner', { averageScore: summary.averageScore, attempts: summary.totalAttempts }],
                        ['Cohort peers', benchmarks.cohort],
                        ['University peers', benchmarks.tenant],
                        ['Global peers', benchmarks.global],
                      ].map(([label, benchmark]) => (
                        <Stack key={label} direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 800 }}>{label}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {benchmark?.learners !== undefined
                                ? `${benchmark.learners || 0} learner${benchmark.learners === 1 ? '' : 's'} · ${benchmark.attempts || 0} attempts`
                                : `${benchmark?.attempts || 0} attempt${benchmark?.attempts === 1 ? '' : 's'}`}
                            </Typography>
                          </Box>
                          <Chip
                            label={formatScore(benchmark?.averageScore)}
                            color={benchmark?.averageScore === null || benchmark?.averageScore === undefined ? 'default' : 'primary'}
                            variant="outlined"
                            sx={{ fontWeight: 900 }}
                          />
                        </Stack>
                      ))}
                    </Stack>
                    {(benchmarks.differenceFromCohort !== null || benchmarks.differenceFromGlobal !== null) && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        {benchmarks.differenceFromCohort !== null && `${benchmarks.differenceFromCohort > 0 ? '+' : ''}${benchmarks.differenceFromCohort} points vs cohort`}
                        {benchmarks.differenceFromCohort !== null && benchmarks.differenceFromGlobal !== null ? ' · ' : ''}
                        {benchmarks.differenceFromGlobal !== null && `${benchmarks.differenceFromGlobal > 0 ? '+' : ''}${benchmarks.differenceFromGlobal} points vs global`}
                      </Alert>
                    )}
                  </Paper>
                </Grid>
                <Grid item xs={12} md={7}>
                  <Grid container spacing={2} sx={{ height: '100%' }}>
                    <Grid item xs={12} sm={6}>
                      <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 3, height: '100%' }}>
                        <Stack direction="row" spacing={1} alignItems="center"><CheckCircleRoundedIcon color="success" /><Typography variant="h6" sx={{ fontWeight: 900 }}>Demonstrated strengths</Typography></Stack>
                        <Stack spacing={1.25} sx={{ mt: 2 }}>
                          {[...(strengths.specialties || []), ...(strengths.domains || [])].slice(0, 6).map((item) => (
                            <Stack key={`${item.key || ''}-${item.label}`} direction="row" justifyContent="space-between" spacing={1}>
                              <Typography variant="body2">{item.label}</Typography><Typography variant="body2" sx={{ fontWeight: 900 }}>{formatScore(item.averageScore)}</Typography>
                            </Stack>
                          ))}
                          {!strengths.specialties?.length && !strengths.domains?.length && <Typography variant="body2" color="text.secondary">Complete more cases to identify reliable strengths.</Typography>}
                        </Stack>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 3, height: '100%' }}>
                        <Stack direction="row" spacing={1} alignItems="center"><ErrorOutlineRoundedIcon color="warning" /><Typography variant="h6" sx={{ fontWeight: 900 }}>Development priorities</Typography></Stack>
                        <Stack spacing={1.25} sx={{ mt: 2 }}>
                          {[...(priorities.specialties || []), ...(priorities.domains || [])].slice(0, 6).map((item) => (
                            <Stack key={`${item.key || ''}-${item.label}`} direction="row" justifyContent="space-between" spacing={1}>
                              <Typography variant="body2">{item.label}</Typography><Typography variant="body2" sx={{ fontWeight: 900 }}>{formatScore(item.averageScore)}</Typography>
                            </Stack>
                          ))}
                          {!priorities.specialties?.length && !priorities.domains?.length && <Typography variant="body2" color="text.secondary">At least two observations are needed before showing a priority.</Typography>}
                        </Stack>
                      </Paper>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>Recurring omissions</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Deterministic patterns from the last ten relevant attempts. No AI call or extra token cost.
                </Typography>
                {recurringOmissions?.length ? (
                  <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                    {recurringOmissions.map((omission) => (
                      <Grid item xs={12} md={6} key={omission.label}>
                        <Alert severity={omission.rate >= 75 ? 'warning' : 'info'}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{omission.message}</Typography>
                        </Alert>
                      </Grid>
                    ))}
                  </Grid>
                ) : <Alert severity="success" sx={{ mt: 2 }}>No repeated low-scoring omissions meet the evidence threshold.</Alert>}
              </Paper>

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}><MetricList title="Specialties" rows={analytics.specialties || []} emptyMessage="No specialty evidence yet." /></Grid>
                <Grid item xs={12} md={4}><MetricList title="Marking domains" rows={analytics.domains || []} emptyMessage="No domain evidence yet." /></Grid>
                <Grid item xs={12} md={4}><MetricList title="Presenting complaints" rows={analytics.presentingComplaints || []} emptyMessage="No presenting complaint evidence yet." /></Grid>
              </Grid>

              <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 3 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 900 }}>Viva performance</Typography>
                    <Typography variant="body2" color="text.secondary">Uses the examiner-question scores already stored with each attempt.</Typography>
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip label={formatScore(viva?.averageScore)} color="primary" sx={{ fontWeight: 900 }} />
                    <Typography variant="caption" color="text.secondary">{viva?.answeredQuestions || 0} scored answers</Typography>
                  </Stack>
                </Stack>
                {viva?.byQuestion?.length ? (
                  <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                    {viva.byQuestion.map((question) => (
                      <Grid item xs={12} sm={6} md={4} key={question.label}>
                        <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'grey.50' }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{question.label}</Typography>
                          <Typography variant="caption" color="text.secondary">{formatScore(question.averageScore)} · {question.count} answer{question.count === 1 ? '' : 's'}</Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                ) : <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>No scored viva answers are available yet.</Typography>}
              </Paper>

              <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
                <Box sx={{ p: 2.25 }}>
                  <Typography variant="h6" sx={{ fontWeight: 900 }}>Performance by case</Typography>
                  <Typography variant="body2" color="text.secondary">Multiple attempts are ordered chronologically so improvement is visible.</Typography>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead><TableRow><TableCell>Case</TableCell><TableCell>Specialty</TableCell><TableCell align="right">Attempts</TableCell><TableCell align="right">Latest</TableCell><TableCell align="right">Change</TableCell><TableCell align="right">Peer benchmarks</TableCell></TableRow></TableHead>
                    <TableBody>
                      {groupedCaseRows.map((caseRow) => (
                        <TableRow key={caseRow.caseId} hover>
                          <TableCell><Typography variant="body2" sx={{ fontWeight: 800 }}>{caseRow.title}</Typography><Typography variant="caption" color="text.secondary">{caseRow.presentingComplaint}</Typography></TableCell>
                          <TableCell>{caseRow.specialty || '—'}</TableCell>
                          <TableCell align="right">{caseRow.attemptCount}</TableCell>
                          <TableCell align="right">{formatScore(caseRow.latestScore)}</TableCell>
                          <TableCell align="right">{caseRow.scoreChange === null ? '—' : `${caseRow.scoreChange > 0 ? '+' : ''}${caseRow.scoreChange} pts`}</TableCell>
                          <TableCell align="right">
                            <Stack spacing={0.25} alignItems="flex-end">
                              {[
                                ['Cohort', caseRow.benchmarks?.cohort, caseRow.differences?.cohort],
                                ['University', caseRow.benchmarks?.tenant, caseRow.differences?.tenant],
                                ['Global', caseRow.benchmarks?.global || caseRow.benchmark, caseRow.differences?.global ?? caseRow.differenceFromBenchmark],
                              ].map(([label, benchmark, difference]) => (
                                <Typography key={label} variant="caption" color="text.secondary">
                                  {label}: <Box component="span" sx={{ color: 'text.primary', fontWeight: 800 }}>{formatScore(benchmark?.averageScore)}</Box>
                                  {difference !== null && difference !== undefined ? ` (${difference > 0 ? '+' : ''}${difference} pts)` : ''}
                                </Typography>
                              ))}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                      {!groupedCaseRows.length && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 5 }}>No completed cases yet.</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Stack>
          ) : (
            <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow><TableCell>Case</TableCell><TableCell>Specialty</TableCell><TableCell>Completed</TableCell><TableCell align="right">Score</TableCell><TableCell>Feedback status</TableCell><TableCell align="right" /></TableRow>
                  </TableHead>
                  <TableBody>
                    {histories.map((history) => (
                      <TableRow key={history.id} hover>
                        <TableCell><Typography variant="body2" sx={{ fontWeight: 800 }}>{history.title}</Typography><Typography variant="caption" color="text.secondary">{history.presentingComplaint || history.sessionMode}</Typography></TableCell>
                        <TableCell>{history.specialty || '—'}</TableCell>
                        <TableCell>{formatDate(history.completedAt, true)}</TableCell>
                        <TableCell align="right">{formatScore(history.overallPercentage)}</TableCell>
                        <TableCell><Chip size="small" label={feedbackLabels[history.feedback?.status] || 'No feedback'} color={history.feedback?.status === 'resolved' ? 'success' : history.feedback?.status === 'follow_up_requested' ? 'warning' : 'default'} /></TableCell>
                        <TableCell align="right"><Button component={RouterLink} to={`/history/${history.id}`} endIcon={<ArrowForwardRoundedIcon />}>Open full history</Button></TableCell>
                      </TableRow>
                    ))}
                    {!histories.length && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 6 }}>This learner has not completed any cases yet.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </TableContainer>
              {historyPagination.hasMore && (
                <Box sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider', textAlign: 'center' }}>
                  <Button onClick={loadOlderHistories} disabled={loadingOlderHistories}>
                    {loadingOlderHistories ? 'Loading older cases…' : `Load older cases (${histories.length} of ${historyPagination.total})`}
                  </Button>
                </Box>
              )}
            </Paper>
          )}
        </Box>
      </Paper>
    </Container>
  );
}

export default StudentDetailPage;
